// Image Link Generator API Service

import { apiFetch } from '../../lib/apiFetch.js';

const API_BASE_URL = 'https://zs58uzhuck.execute-api.ap-south-1.amazonaws.com';
const MAX_CONCURRENT_UPLOADS = 3;

export const imageLinkApi = {
  // Generate a run ID from username + timestamp
  createRunId(username) {
    const user = (username || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
    const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15);
    return `${user}_${ts}`;
  },

  // Get presigned S3 PUT URLs for a list of files
  async getUploadUrls(files, runId) {
    const fileMetadata = files.map(f => ({ name: f.name, size: f.size }));
    const response = await apiFetch(`${API_BASE_URL}/api/get-upload-urls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files: fileMetadata, run_id: runId })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to get upload URLs: ${response.status}`);
    }
    return response.json();
  },

  // Upload a single file directly to S3
  async uploadFileToS3(presignedUrl, file, contentType) {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed for ${file.name}: ${response.status}`);
    }
    return response;
  },

  // Orchestrate upload with concurrency control
  async uploadFiles(files, runId, onProgress) {
    const { results } = await this.getUploadUrls(files, runId);
    const total = files.length;
    let completed = 0;
    if (onProgress) onProgress({ completed: 0, total });

    const fileMap = {};
    files.forEach(f => { fileMap[f.name] = f; });
    const uploadResults = [];
    const queue = [...results];
    const executing = new Set();

    const enqueue = async () => {
      while (queue.length > 0 && executing.size < MAX_CONCURRENT_UPLOADS) {
        const result = queue.shift();
        const file = fileMap[result.file];

        if (result.status !== 'ready' || !result.upload_url) {
          uploadResults.push(result);
          completed++;
          if (onProgress) onProgress({ completed, total });
          continue;
        }

        const promise = this.uploadFileToS3(result.upload_url, file, result.content_type)
          .then(() => {
            const viewUrl = result.view_url.startsWith('/')
              ? `${API_BASE_URL}${result.view_url}`
              : result.view_url;
            uploadResults.push({
              file: result.file, s3_key: result.s3_key,
              url: viewUrl, status: 'ok'
            });
          })
          .catch((err) => {
            uploadResults.push({
              file: result.file, s3_key: result.s3_key,
              url: '', status: `Failed: ${err.message}`
            });
          })
          .finally(() => {
            executing.delete(promise);
            completed++;
            if (onProgress) onProgress({ completed, total });
          });

        executing.add(promise);
        if (executing.size >= MAX_CONCURRENT_UPLOADS) {
          await Promise.race(executing);
        }
      }
    };

    await enqueue();
    await Promise.all(executing);
    return uploadResults;
  },

  // Save results to S3 and get run metadata
  async saveResults(runId, user, results) {
    const response = await apiFetch(`${API_BASE_URL}/api/save-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId, user, results })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to save results: ${response.status}`);
    }
    return response.json();
  },

  // Download CSV by run_id
  async downloadCsv(runId) {
    const response = await apiFetch(`${API_BASE_URL}/api/download-csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to download CSV: ${response.status}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${runId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  // List recent runs
  async listRuns() {
    const response = await apiFetch(`${API_BASE_URL}/api/runs`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || `Failed to list runs: ${response.status}`);
    }
    return response.json();
  }
};
